import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV_LINKS } from "../content/site";

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <>
      <motion.header
        data-testid="main-nav"
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 inset-x-0 z-[80] transition-[background-color,border-color,backdrop-filter] duration-300 ${
          scrolled ? "glass border-b border-slate-100" : "bg-transparent border-b border-transparent"
        }`}
      >
        <nav className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between transition-[padding] duration-300 ${scrolled ? "py-3" : "py-5"}`} aria-label="Main navigation">
          <a href="#home" data-testid="nav-logo" className="flex items-center group" onClick={() => setOpen(false)}>
            <img src="/logo.png" alt="WebStudios — website design, hosting and maintenance"
              className="h-12 w-auto object-contain transition-transform duration-300 group-hover:scale-[1.03]" />
          </a>
          <div className="hidden lg:flex items-center gap-7">
            {NAV_LINKS.map((l) => (
              <a key={l.href} href={l.href} data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="text-sm text-slate-500 hover:text-slate-900 transition-colors duration-200">
                {l.label}
              </a>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <a href="#contact" data-testid="nav-quote-cta"
              className="hidden sm:inline-flex items-center gap-1.5 bg-[#0055FF] hover:bg-[#0044CC] text-white text-sm font-medium px-5 py-2.5 rounded-full transition-colors duration-200 glow-blue">
              Get a Free Quote <ArrowUpRight className="w-4 h-4" />
            </a>
            <button data-testid="mobile-menu-toggle" aria-label={open ? "Close menu" : "Open menu"} aria-expanded={open}
              onClick={() => setOpen(!open)}
              className="lg:hidden w-10 h-10 flex items-center justify-center rounded-full bg-white border border-slate-200 text-slate-900 soft-shadow">
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </nav>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[70] lg:hidden bg-white/97 backdrop-blur-2xl pt-24 px-6"
          >
            <div className="flex flex-col gap-1">
              {NAV_LINKS.map((l, i) => (
                <motion.a
                  key={l.href}
                  href={l.href}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => setOpen(false)}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i, duration: 0.3 }}
                  className="font-display text-3xl font-semibold text-slate-800 hover:text-slate-900 py-3 border-b border-slate-100 transition-colors"
                >
                  {l.label}
                </motion.a>
              ))}
              <motion.a
                href="#contact"
                data-testid="mobile-quote-cta"
                onClick={() => setOpen(false)}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="mt-8 inline-flex items-center justify-center gap-2 bg-[#0055FF] text-white font-semibold px-6 py-4 rounded-full glow-blue"
              >
                Get a Free Quote <ArrowUpRight className="w-5 h-5" />
              </motion.a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
